import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePublicCategories } from 'modules/test/hooks';
import { publicTestCategoryPath } from 'modules/test/public-categories';
import { cn } from 'lib/utils';
import { catalogCategories } from '../catalogCategories';
import { categorySurface, categoryTheme } from '../categoryThemes';

/** A 12-column desktop row holds four wide tiles, then three medium ones. */
const spanClass = (index: number) => (index < 4 ? 'lg:col-span-3' : 'lg:col-span-4');

/** "Yo'nalishni tanlang": one tinted tile per test category, each in its own accent, linking to that category. */
export function HomeCategories() {
  const { t, i18n } = useTranslation();
  const { data } = usePublicCategories();
  const items = catalogCategories(data?.data, t, i18n.language).filter(({ id }) => id !== 'all');
  const counts = new Map((data?.data ?? []).map((category) => [category.id, category.testCount]));

  if (items.length === 0) return null;

  return (
    <section id="portal-home-categories" aria-labelledby="home-categories-title">
      <div className="mb-4 flex items-end justify-between gap-3">
        <h2 id="home-categories-title" className="font-display text-[22px] font-extrabold tracking-[-0.03em] text-foreground">
          {t('portal.home_choose_direction')}
        </h2>
        <Link
          to="/portal/tests"
          className="rounded-md text-[13.5px] font-bold text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {t('portal.home_all_tests')} →
        </Link>
      </div>
      <ul className="grid grid-cols-2 gap-2.5 md:gap-5 lg:grid-cols-12">
        {items.map(({ id, label }, index) => {
          const { accent, icon: Icon } = categoryTheme(id);
          const testCount = counts.get(id);
          const surface = categorySurface(accent);
          return (
            <li
              key={id}
              // An odd last tile on the phone grid runs the full row.
              className={cn(spanClass(index), index === items.length - 1 && items.length % 2 === 1 && 'col-span-2 lg:col-span-4')}
            >
              <Link
                to={publicTestCategoryPath(id)}
                style={{ background: surface.background, borderColor: surface.borderColor }}
                className="relative flex min-h-[128px] flex-col justify-between overflow-hidden rounded-2xl border p-3.5 transition-[transform,box-shadow,border-color] duration-200 hover:-translate-y-1 hover:shadow-[0_24px_36px_-26px_rgba(16,24,43,0.45)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring motion-reduce:transition-none motion-reduce:hover:translate-y-0 md:min-h-[188px] md:rounded-[20px] md:p-5"
              >
                <Icon
                  aria-hidden="true"
                  strokeWidth={1.3}
                  className="pointer-events-none absolute -right-3.5 -top-3 h-[92px] w-[92px] rotate-[-8deg] opacity-[0.15] md:-right-5 md:-top-[18px] md:h-[132px] md:w-[132px]"
                  style={{ color: accent }}
                />
                <span
                  aria-hidden="true"
                  className="relative flex h-9 w-9 items-center justify-center rounded-[11px] text-white md:h-11 md:w-11 md:rounded-[13px]"
                  style={{ background: accent }}
                >
                  <Icon className="h-[19px] w-[19px] md:h-[22px] md:w-[22px]" strokeWidth={1.9} />
                </span>
                <span className="relative">
                  <span className="block font-display text-[15px] font-extrabold tracking-[-0.03em] text-foreground md:text-[19px]">{label}</span>
                  {testCount !== undefined && (
                    <span className="mt-0.5 block text-[11.5px] text-foreground/70 md:mt-1 md:text-[12.5px]">
                      {t('portal.home_tests_count', { count: testCount })}
                    </span>
                  )}
                </span>
              </Link>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

export default HomeCategories;
